import React from "react";
import {
  Card,
  CardContent,
  Box,
  useTheme,
  Tooltip,
  Button,
} from "@mui/material";
import CustomTypography from "../common/form/CustomTypography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface TypeFilterCardProps {
  variant?: "card" | "linear" | "standard";
  title: string;
  value: number | string;
  icon: string;
  color: string;
  message?: string;
  onClick?: (title: string) => void;
  percentageChange?: string;
  comparisonText?: string;
}

const TypeFilterCard: React.FC<TypeFilterCardProps> = ({
  variant = "card",
  title,
  value,
  icon,
  color,
  message,
  onClick,
  percentageChange = "+0.00%",
  comparisonText = "vs last 30 days",
}) => {
  const theme = useTheme();

  if (variant === "standard") {
    return (
      <Card sx={{ borderRadius: 2, backgroundColor: "#F9F9F9", minWidth: 220 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 62,
                height: 62,
                backgroundColor: "#F3F4F6",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={icon}
                alt={`${title} icon`}
                style={{ width: 40, height: 40 }}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.greyTextColor}
              >
                {title}
              </CustomTypography>
              <CustomTypography
                variant="heading4"
                style={{ fontWeight: 700, color: "#121212" }}
              >
                ₹{value.toLocaleString("en-IN")}
              </CustomTypography>
            </Box>
          </Box>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                paddingLeft: "78px",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#E6F7EC",
                  color: "#30A46C",
                  fontWeight: 600,
                  px: 1,
                  py: 0.3,
                  borderRadius: 2,
                  fontSize: "0.75rem",
                }}
              >
                {percentageChange}
              </Box>
              <CustomTypography variant="caption" color="text.secondary">
                {comparisonText}
              </CustomTypography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return variant === "card" ? (
    <Card sx={{ minWidth: 160, borderRadius: 2, height: 105, pb: 1 }}>
      <CardContent
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <CustomTypography
              variant="heading8"
              fontWeight={700}
              color={theme.palette.customColors.tableGrayHeadingColor}
              style={{ minHeight: "2.2rem", lineHeight: "1.3" }}
            >
              {title}
            </CustomTypography>

            {message && (
              <Tooltip title={message}>
                <InfoOutlinedIcon
                  sx={{
                    cursor: "pointer",
                    color: theme.palette.grey[600],
                    width: "18px",
                  }}
                />
              </Tooltip>
            )}
          </Box>
          {onClick ? (
            <Button
              onClick={() => onClick(title)}
              style={{ padding: 0, margin: 0, justifyContent: "flex-start" }}
            >
              <CustomTypography variant="heading5">{value}</CustomTypography>
            </Button>
          ) : (
            <CustomTypography variant="heading5">{value}</CustomTypography>
          )}
        </Box>
        <Box
          sx={{
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 50,
            height: 50,
            paddingTop: 1,
          }}
        >
          <img
            src={icon}
            alt={`${title} icon`}
            style={{ width: 45, height: 45 }}
          />
        </Box>
      </CardContent>
    </Card>
  ) : (
    <>
      {Number(value) > 0 ? (
        <Card
          sx={{
            maxHeight: 99,
            borderRadius: 2,
            border: "1px solid #E3E3E3",
            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
          }}
        >
          <CardContent
            sx={{
              display: "flex",
              alignItems: "center",
              flex: "inline-block",
              paddingLeft: 5,
              gap: 7,
            }}
          >
            <>
              <Box
                sx={{
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "1vw",
                }}
              >
                <img
                  src={icon}
                  alt={`${title} icon`}
                  style={{ width: "auto", height: "auto" }}
                />
              </Box>
              <Box
                sx={{ display: "flex", flexDirection: "column", width: "100%" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {onClick ? (
                    <Button
                      onClick={() => onClick(title)}
                      style={{
                        padding: 0,
                        margin: 0,
                        justifyContent: "flex-start",
                      }}
                    >
                      <CustomTypography variant="heading5">
                        {value}
                      </CustomTypography>
                    </Button>
                  ) : (
                    <CustomTypography variant="heading5">
                      {value}
                    </CustomTypography>
                  )}
                  {message && (
                    <Tooltip title={message}>
                      <InfoOutlinedIcon
                        sx={{
                          cursor: "pointer",
                          color: theme.palette.grey[600],
                          width: "18px",
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
                <CustomTypography
                  variant="heading8"
                  fontWeight={700}
                  color={theme.palette.customColors.blackBorder}
                  style={{
                    flexWrap: "wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {title}
                </CustomTypography>
              </Box>
            </>
          </CardContent>
        </Card>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 3.5,
            height: "400px",
          }}
        >
          <CustomTypography variant="heading8" color="textSecondary">
            Nothing to show for the selected filters
          </CustomTypography>
        </Box>
      )}
    </>
  );
};

export default TypeFilterCard;
